<script setup lang="ts">
import { PhTextT, PhSpeakerSimpleHigh, PhSpeakerHigh, PhFlowArrow } from '@phosphor-icons/vue'
import { Switch } from '@/components/ui/switch'
import FontSelector from '@/components/quran/FontSelector.vue'
import ReciterSelector from '@/components/quran/ReciterSelector.vue'

const props = defineProps<{
  quranState: {
    selectedFont: string
    selectedReciter: string
    showTransliteration: boolean
    showTranslation: boolean
    showAudio: boolean
  }
}>()

const emit = defineEmits<{
  'update:selectedFont': [value: string]
  'update:selectedReciter': [value: string]
  'update:showTransliteration': [value: boolean]
  'update:showTranslation': [value: boolean]
  'update:showAudio': [value: boolean]
}>()

</script>

<template>
  <div class="mt-6 space-y-6">
    <div class="space-y-2">
      <label class="flex items-center gap-2 text-sm font-medium text-foreground">
        <PhTextT :size="18" class="text-moss-500" />
        Quran Font
      </label>
      <FontSelector
        :selected-font="props.quranState.selectedFont"
        @update:model-value="emit('update:selectedFont', $event)"
      />
      <p class="text-xs leading-snug text-muted-foreground">
        IndoPak uses the South Asian script layout. Tajweed uses Medina-style script with colored tajweed marks.
      </p>
    </div>

    <div class="space-y-4">
      <h3 class="text-sm font-semibold text-foreground">Audio</h3>
      <p class="text-xs leading-snug text-muted-foreground">
        Verse and surah players on the page use these settings when audio is enabled below.
      </p>

      <div class="space-y-2">
        <label class="flex items-center gap-2 text-sm font-medium text-foreground">
          <PhSpeakerSimpleHigh :size="18" class="text-moss-500" />
          Reciter
        </label>
        <ReciterSelector
          :selected-reciter="props.quranState.selectedReciter"
          @update:model-value="emit('update:selectedReciter', $event)"
        />
      </div>

      <div class="flex items-center justify-between">
        <label class="flex items-center gap-2 text-sm font-medium text-foreground">
          <PhSpeakerHigh :size="16" class="text-muted-foreground" />
          Show audio players
        </label>
        <Switch
          :model-value="props.quranState.showAudio"
          @update:model-value="emit('update:showAudio', Boolean($event))"
        />
      </div>
    </div>

    <div class="space-y-4">
      <h3 class="text-sm font-semibold text-foreground">Display</h3>

      <div class="flex items-center justify-between">
        <label class="flex items-center gap-2 text-sm font-medium text-foreground">
          <PhTextT :size="16" class="text-muted-foreground" />
          Transliteration
        </label>
        <Switch
          :model-value="props.quranState.showTransliteration"
          @update:model-value="emit('update:showTransliteration', Boolean($event))"
        />
      </div>

      <div class="flex items-center justify-between">
        <label class="flex items-center gap-2 text-sm font-medium text-foreground">
          <PhFlowArrow :size="16" class="text-muted-foreground" />
          Translation
        </label>
        <Switch
          :model-value="props.quranState.showTranslation"
          @update:model-value="emit('update:showTranslation', Boolean($event))"
        />
      </div>
    </div>
  </div>
</template>
